import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { EMPLOYEE_STAFF_LEVEL_OPTIONS } from '../../types/employee-training.type';
import {
  trainingFilterBarSx,
  trainingToolbarHintSx,
  trainingToolbarLevelSelectSx,
  trainingToolbarSearchFieldSx,
} from './trainingTable.styles';

export type TrainingTableToolbarProps = {
  filterText: string;
  onFilterTextChange: (value: string) => void;
  levelFilter?: string[];
  onLevelFilterChange?: (value: string[]) => void;
  /** Shown on the right (e.g. scope hint for server-paged data) */
  hint?: string;
};

function normalizeMultiSelectValue(value: string | string[]): string[] {
  return typeof value === 'string' ? value.split(',') : value;
}

const TrainingTableToolbar: React.FC<TrainingTableToolbarProps> = ({
  filterText,
  onFilterTextChange,
  levelFilter,
  onLevelFilterChange,
  hint,
}) => {
  const { t } = useTranslation();
  const showLevelFilter = onLevelFilterChange != null;
  const selectedLevels = levelFilter ?? [];

  const handleLevelChange = (event: SelectChangeEvent<string[]>) => {
    onLevelFilterChange?.(normalizeMultiSelectValue(event.target.value));
  };

  const renderLevelFilterValue = (selected: string[]): React.ReactNode => {
    if (selected.length === 0) return t('employeeTraining.allLevels');
    if (selected.length <= 2) {
      return selected
        .map((lvl) => t(`employeeTraining.staffLevels.${lvl}`, { defaultValue: lvl }))
        .join(', ');
    }
    return t('employeeTraining.levelsCount', { count: selected.length });
  };

  const levelLabel = t('employeeTraining.levelFilter');

  return (
    <Box role="toolbar" aria-label={t('employeeTraining.filterPlaceholder')} sx={trainingFilterBarSx}>
      <TextField
        size="small"
        placeholder={t('employeeTraining.filterPlaceholder')}
        value={filterText}
        onChange={(e) => onFilterTextChange(e.target.value)}
        sx={trainingToolbarSearchFieldSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />
      {showLevelFilter ? (
        <FormControl size="small" sx={trainingToolbarLevelSelectSx}>
          <InputLabel id="training-level-filter-label">{levelLabel}</InputLabel>
          <Select
            labelId="training-level-filter-label"
            multiple
            value={selectedLevels}
            onChange={handleLevelChange}
            input={<OutlinedInput label={levelLabel} />}
            renderValue={renderLevelFilterValue}
          >
            {EMPLOYEE_STAFF_LEVEL_OPTIONS.map((lvl) => (
              <MenuItem key={lvl} value={lvl} dense>
                <Checkbox checked={selectedLevels.includes(lvl)} size="small" />
                <ListItemText
                  primary={t(`employeeTraining.staffLevels.${lvl}`, { defaultValue: lvl })}
                  primaryTypographyProps={{ fontSize: '0.875rem', lineHeight: 1.5 }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={trainingToolbarHintSx}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
};

export default TrainingTableToolbar;
